import type { ReactNode } from 'react'
import { MediaArt } from '../../components/ui'
import { useTenant } from '../../tenant/useTenant'
import { cn } from '../../utils/cn'

interface AuthCardProps {
  /** Material Symbols glyph shown above the title. */
  icon?: string
  title: string
  subtitle?: ReactNode
  children: ReactNode
  /** Sign-in / sign-up cross-links, rendered under a hairline inside the canvas. */
  footer?: ReactNode
  /** `wide` matches the roomier canvas Stitch uses for multi-column forms. */
  width?: 'md' | 'lg' | 'xl'
  /** Success screens seat the glyph in a tonal disc; step screens show it bare. */
  iconStyle?: 'plain' | 'tile'
}

const WIDTHS = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' } as const

/**
 * The glyph above an auth heading. `password_updated_successfully_1` and
 * `cancellation_confirmed_1` both seat it in an 80px tonal disc; the step
 * screens (`verify_sms_otp_oroshi_beauty`) show it bare at 32px.
 */
export function AuthIcon({ name, style = 'plain' }: { name: string; style?: 'plain' | 'tile' }) {
  if (style === 'tile') {
    return (
      <span
        aria-hidden="true"
        className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/5 text-primary"
      >
        <span className="material-symbols-outlined text-[36px]">{name}</span>
      </span>
    )
  }
  return (
    <span className="material-symbols-outlined mb-6 block text-[32px] text-primary" aria-hidden="true">
      {name}
    </span>
  )
}

/**
 * The Stitch "content canvas": a single tonal panel on the page background,
 * generous 32/48px padding, a hairline in the primary at 10%, and a centred
 * editorial header. Matches `create_your_password_oroshi_beauty_1`,
 * `verify_sms_otp_oroshi_beauty` and `password_updated_successfully_1`.
 *
 * The vendor wordmark is deliberately not repeated above the canvas: those Stitch
 * screens that keep the top nav (reset_your_password_1, password_updated_1) do not
 * repeat it either, and our auth routes always render inside the app shell.
 */
export function AuthCard({
  icon,
  title,
  subtitle,
  children,
  footer,
  width = 'md',
  iconStyle = 'plain',
}: AuthCardProps) {
  return (
    <div className="container-page flex justify-center py-section-gap-md md:py-section-gap-lg">
      <div className={cn('w-full', WIDTHS[width])}>
        <div className="rounded-xl border border-primary/10 bg-surface-container-lowest p-8 md:p-12">
          <header className="mb-12 text-center">
            {icon && <AuthIcon name={icon} style={iconStyle} />}
            <h1 className="font-display text-headline-md text-primary">{title}</h1>
            {subtitle && (
              <p className="mx-auto mt-4 max-w-sm font-body-lg text-body-lg text-on-surface-variant">{subtitle}</p>
            )}
          </header>

          {children}

          {footer && (
            <div className="mt-12 border-t border-primary/10 pt-8 text-center font-body-md text-body-md text-on-surface-variant">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface AuthSplitProps {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** Stitch puts the image left on sign-in and right on the reset request. */
  imageSide?: 'left' | 'right'
}

/**
 * The Stitch split canvas: a full-bleed image half beside a single form column.
 * Matches `sign_in_oroshi_beauty_1` and `reset_your_password_oroshi_beauty_1`.
 *
 * The image is the vendor's own cover; a vendor who has not uploaded one gets
 * the drawn salon interior, the same fallback the home hero uses.
 */
export function AuthSplit({ title, subtitle, children, footer, imageSide = 'left' }: AuthSplitProps) {
  const { bootstrap } = useTenant()
  const cover = bootstrap.theme.cover

  const image = (
    <div
      aria-hidden="true"
      className={cn(
        'relative hidden overflow-hidden bg-surface-container-low md:block md:w-1/2',
        imageSide === 'right' && 'md:order-last',
      )}
    >
      {cover ? (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${cover}')` }} />
      ) : (
        <MediaArt kind="cover" className="absolute inset-0" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
    </div>
  )

  return (
    <div className="flex min-h-[70vh] flex-col md:flex-row">
      {image}
      <div className="flex w-full items-center justify-center bg-surface px-margin-mobile py-section-gap-md md:w-1/2 md:px-gutter md:py-section-gap-lg">
        <div className="w-full max-w-md space-y-12">
          <header className="space-y-4 text-center md:text-left">
            <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">{title}</h1>
            {subtitle && <p className="font-body-lg text-body-lg text-on-surface-variant">{subtitle}</p>}
          </header>

          {children}

          {footer && (
            <div className="border-t border-primary/10 pt-8 text-center font-body-md text-body-md text-on-surface-variant">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface AuthColumnProps {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
}

/**
 * The bare centred column of `set_up_account_enter_phone_number`: no panel at
 * all, just a narrow measure floating in the page's whitespace under a large
 * Display heading. Stitch reserves this for the first step of a flow, where the
 * emptiness is the point.
 */
export function AuthColumn({ title, subtitle, children, footer }: AuthColumnProps) {
  return (
    <div className="container-page flex justify-center py-section-gap-md md:py-section-gap-lg">
      <div className="w-full max-w-md space-y-12">
        <header className="space-y-4 text-center">
          <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">{title}</h1>
          {subtitle && (
            <p className="mx-auto max-w-[80%] font-body-lg text-body-lg text-on-surface-variant">{subtitle}</p>
          )}
        </header>

        {children}

        {footer && (
          <div className="border-t border-primary/10 pt-8 text-center font-body-md text-body-md text-on-surface-variant">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
